import themeConfig from '@configs/themeConfig';
import { getLocalStorageItem, getStorageItem, setLocalStorageItem, setStorageItem } from '@src/utils/helperFunctions';
// eslint-disable-next-line import/no-cycle
import { layoutSidebarMenuHoverVar, layoutSidebarMenuVar, layoutSkinStateVar, layoutStateVar } from '@store/reactiveVars';

export interface ILayoutState {
  skin: string;
  isRTL: boolean;
  layout: string,
  lastLayout: string,
  menuCollapsed: boolean,
  menuHover: boolean,
  footerType: string,
  navbarType: string,
  menuHidden: boolean,
  contentWidth: string,
  routerTransition: string,
  navbarColor: string,
}

const initialMenuCollapsed = (): boolean => (
  getStorageItem<boolean>('menuCollapsed') ?? themeConfig.layout.menu.isCollapsed
);

const initialDirection = (): boolean => (
  getStorageItem<boolean>('direction') ?? themeConfig.layout.isRTL
);

export const initialSkin = (): string => (
  // Prefer localStorage (cross-session). Fall back to legacy sessionStorage value so
  // returning users on the new build don't lose the preference they set on the old one.
  getLocalStorageItem<string>('skin') ?? getStorageItem<string>('skin') ?? themeConfig.layout.skin
);

export const layoutInitialState: ILayoutState = {
  skin: initialSkin(),
  isRTL: initialDirection(),
  layout: themeConfig.layout.type,
  lastLayout: themeConfig.layout.type,
  menuCollapsed: initialMenuCollapsed(),
  menuHover: false,
  footerType: themeConfig.layout.footer.type,
  navbarType: themeConfig.layout.navbar.type,
  menuHidden: themeConfig.layout.menu.isHidden,
  contentWidth: themeConfig.layout.contentWidth,
  routerTransition: themeConfig.layout.routerTransition,
  navbarColor: themeConfig.layout.navbar.backgroundColor
};

// Single immutable updater. Apollo's makeVar does broadcast on every call regardless of
// equality, but mutating the previous value in place leaks the change to anyone holding
// a stale reference (e.g. selectors, memoized components). Always emit a fresh object.
const patchLayout = (patch: Partial<ILayoutState>): void => {
  layoutStateVar({ ...layoutStateVar(), ...patch });
};

export const handleRTL = (payload: boolean) => patchLayout({ isRTL: payload });

export const handleSkin = (payload: string) => {
  layoutSkinStateVar(payload);
  // Persist across sessions. Clear the legacy session-scoped value so the two
  // storages don't drift apart for users who set preferences on older builds.
  setLocalStorageItem('skin', payload);
  try { sessionStorage.removeItem('skin'); } catch { /* private mode */ }
};

export const handleLayout = (payload: string) => patchLayout({ layout: payload });

export const handleFooterType = (payload: string) => patchLayout({ footerType: payload });

export const handleNavbarType = (payload: string) => patchLayout({ navbarType: payload });

export const handleMenuHidden = (payload: boolean) => patchLayout({ menuHidden: payload });

export const handleLastLayout = (payload: string) => patchLayout({ lastLayout: payload });

export const handleNavbarColor = (payload: string) => patchLayout({ navbarColor: payload });

export const handleContentWidth = (payload: string) => patchLayout({ contentWidth: payload });

export const handleMenuCollapsed = (payload: boolean) => {
  layoutSidebarMenuVar(payload);
  setStorageItem('menuCollapsed', payload);
};

export const handleMenuHovered = (payload: boolean) => {
  layoutSidebarMenuHoverVar(payload);
};

export const handleRouterTransition = (payload: string) => (
  patchLayout({ routerTransition: payload })
);
